import { ref, computed } from 'vue';

import { getAuthor as getAuthorFromServer } from '@/shared/api/experience';
import { useLogger } from '@/shared/utils/logger';
import { makeSingleton } from '@/shared/utils/makeSingleton';
import { useWaiters } from '@/shared/utils/useWaiters';

import type { Author } from './models';
import { normalizeAuthor } from './transforms';

export const useAuthorEntity = makeSingleton(() => {
  const logger = useLogger();

  const authorState = ref<Author | null>(null);
  const author = computed(() => authorState.value);

  const isLoadingFailed = ref(false);
  const loadingFailed = computed(() => isLoadingFailed.value);

  const loadAuthor = async (id: number) => {
    if (authorState.value?.id === id) {
      return;
    }

    isLoadingFailed.value = false;

    let response;

    try {
      response = await getAuthorFromServer({ path: { authorId: id } });
    } catch (e) {
      logger.warn('could not load an author by the reason:', e);
      isLoadingFailed.value = true;

      return;
    }

    if (response.data) {
      authorState.value = normalizeAuthor(response.data);
    } else {
      isLoadingFailed.value = true;
    }
  };

  const { processing: loading, execute: getAuthor } = useWaiters(loadAuthor);

  return {
    loading,
    loadingFailed,
    author,
    getAuthor,
  };
});
